'use strict';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Paper from '@mui/material/Paper';
import * as d3 from 'd3';
import IntervalLabel from 'js/components/Prescription/PrescriptionScale/IntervalLabel';
import generateNdviGradient from './generateNdviGradient';
import useEvent from 'js/hooks/useEvent';
import { useLangFile } from 'js/context/LanguageContext';
import PropTypes from 'prop-types';
import useWindowDimensions from 'js/hooks/useWindowDimensions';
import style from 'js/components/Prescription/DraggableIntervals/PrescriptionScale/PrescriptionScale.module.less';
import Box from '@mui/material/Box';
import {
  isSatelliteLayer,
  isSoilLayer,
  isSurveyLayer,
} from 'js/components/Prescription/PrescriptionJob';
import {
  getAllUniqueValues,
  getBulkPrescriptionIntervalArea,
  getEffectiveSoilValues,
  getIntervalUniqueValues,
  getLayerConfig,
  getSoilValuesCount,
} from '../../PrescriptionUtils';
import { renderStandardDemandLegend } from '../../../DemandScale/DemandScale';
import { ClassificationColoringStrategy } from '../../../../model/surveys/SurveyColoringStrategies';
import ViewModeConstants from '../../../../constants/ViewModeConstants';
import { useColoringStrategy } from '../../../../model/surveys/useColoringStrategy';
import { SURVEY_LAYERS } from '../../../../constants/SurveyLayers';
import { SATELLITE_LAYERS } from 'js/constants/SatelliteLayers';
import { isYieldLayer } from '../../PrescriptionJob';
import { getNullIntervalLayout, isNullInterval, NULL_INTERVAL_VALUE } from '../../nullValueRegions';
import { PRESCRIPTION_NULL_INTERVAL_COLOR } from '../../../../constants/PrescriptionConstants';
import { PrescriptionState } from '../../PrescriptionState';
import { usePrescriptionJobOrchestration } from '../../SeedingJob/usePrescriptionJobOrchestration';

const generateSurveyGradient = (context, width, intervals, coloringStrategy, layer, values) => {
  if (!context || !width || !intervals?.length || !coloringStrategy) return null;

  const gradient = context.createLinearGradient(0, 0, width, 0);

  const min = intervals[0].min;
  const max = intervals[intervals.length - 1].max;

  const startColor = coloringStrategy.getColor(min);
  if (startColor) gradient.addColorStop(0, startColor);

  const soilClassLayer = isSoilLayer(layer);

  let steps, proportions;
  if (soilClassLayer) {
    const intervalCounts = intervals.map((interval) => {
      return getIntervalUniqueValues(interval, values).length;
    });

    const totalCount = getAllUniqueValues(values).filter(
      (value) => value !== NULL_INTERVAL_VALUE
    ).length;
    proportions = intervalCounts.map((count) => count / totalCount);

    steps = intervals.length;
  } else {
    steps = 10;
  }

  let currentPos = 0;

  for (let i = 0; i < steps; i++) {
    let nextPos;

    if (soilClassLayer) {
      nextPos = currentPos + proportions[i];
    } else {
      nextPos = (i + 1) / steps;
    }

    const value = soilClassLayer ? intervals[i].min : min + i * ((max - min) / steps);
    const color = coloringStrategy.getColor(value);
    if (!color) continue;

    gradient.addColorStop(currentPos, color);

    if (soilClassLayer) {
      const intervalUniqueValues = getIntervalUniqueValues(intervals[i], values);
      if (intervalUniqueValues.length > 1) {
        for (let j = 1; j < intervalUniqueValues.length; j++) {
          const fraction = j / intervalUniqueValues.length;
          const boundary = currentPos + (nextPos - currentPos) * fraction;

          const prevColor = coloringStrategy.getColor(intervalUniqueValues[j - 1]);
          const currColor = coloringStrategy.getColor(intervalUniqueValues[j]);

          if (prevColor) gradient.addColorStop(boundary, prevColor);
          if (prevColor && currColor) gradient.addColorStop(boundary, currColor);

          if (j === intervalUniqueValues.length - 1 && currColor) {
            gradient.addColorStop(nextPos, currColor);
          }
        }
      } else {
        gradient.addColorStop(nextPos, color);
      }
    } else {
      gradient.addColorStop(nextPos, color);
    }
    currentPos = nextPos;
  }

  const endColor = coloringStrategy.getColor(max);
  if (endColor) gradient.addColorStop(1, endColor);

  return gradient;
};

const padding = 12;

const PrescriptionScale = ({
  layer,
  intervals,
  overrideAreas,
  classificationsEnabled,
  variationsEnabled,
  values,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
}) => {
  const LangFile = useLangFile();
  const dimensions = useWindowDimensions();
  const ctxRef = useRef(null);
  const canvasRef = useRef(null);
  const xMap = useRef(null);
  const forceRedraw = useState({});
  const layerConfig = getLayerConfig(layer);
  const classifier = new ClassificationColoringStrategy(layer, ViewModeConstants.PRESCRIPTION)
    .classifier;
  const normalise = variationsEnabled || layer === SURVEY_LAYERS.ALTITUDE;
  const { prescriptionState } = usePrescriptionJobOrchestration();

  const isBulkMode = isBulkPrescription && !appliedInitialIntervals;
  const effectiveValues = useMemo(
    () => getEffectiveSoilValues(values, layer, isBulkMode, bulkPrescriptionJobs),
    [values, layer, isBulkMode, bulkPrescriptionJobs]
  );
  const hasNullValueInterval = isNullInterval(intervals[0]);
  const sourceIntervals = useMemo(
    () => (hasNullValueInterval ? intervals.slice(1) : intervals),
    [intervals, hasNullValueInterval]
  );
  const { nullWidth } = getNullIntervalLayout(intervals);
  const nullCount =
    hasNullValueInterval && getAllUniqueValues(effectiveValues).includes(NULL_INTERVAL_VALUE)
      ? 1
      : 0;

  const coloringStrategy = useColoringStrategy(
    layer,
    ViewModeConstants.PRESCRIPTION,
    classificationsEnabled,
    normalise,
    values
  );

  // Determine min and max values
  const minValue = Number(sourceIntervals[0].min).toFixed(3);
  const maxValue = Number(intervals[intervals.length - 1].max).toFixed(3);

  // Determine overridden area
  const overriddenArea = overrideAreas
    ? Object.values(overrideAreas).reduce((sum, cur) => sum + cur.area, 0)
    : 0;

  const overriddenAreaString = Number(overriddenArea).toFixed(2);

  const canvasWidth = dimensions.width - 2 * padding;

  // Handlers
  const generateSeparators = useCallback(() => {
    if (!intervals || !xMap.current) {
      return [];
    }

    return intervals.map((interval, idx) => {
      if (idx === intervals.length - 1 || isNullInterval(interval)) return null;
      let upperBound;
      if (isSoilLayer(layer)) {
        const { thisCount, prevCount } = getSoilValuesCount(
          interval,
          intervals.slice(0, idx),
          effectiveValues
        );
        upperBound = prevCount + thisCount - nullCount;
      } else {
        const nextInterval = intervals[idx + 1];
        upperBound = nextInterval ? nextInterval.min : interval.max;
      }

      const xPos = xMap.current(upperBound) - 2;

      return [
        <Box
          key={'handle ' + idx + 1}
          className={style.Handle}
          sx={{
            left: xPos,
          }}
        />,
        <Box
          key={'value ' + idx + 1}
          className={style.Value}
          sx={{
            left: xPos,
          }}
        >
          {isSoilLayer(layer) ? '' : Number(interval.max).toFixed(3)}
        </Box>,
      ];
    });
  }, [intervals, xMap]);

  const generateIntervalSizeLabels = useCallback(() => {
    if (!intervals || !xMap.current) {
      return null;
    }

    return intervals.map((interval, idx, array) => {
      let lowerBound, upperBound;
      if (isSoilLayer(layer)) {
        const { thisCount, prevCount } = getSoilValuesCount(
          interval,
          intervals.slice(0, idx),
          effectiveValues
        );

        lowerBound = prevCount - nullCount;
        upperBound = prevCount + thisCount - nullCount;
      } else {
        const nextInterval = array[idx + 1];
        lowerBound = interval.min;
        upperBound = nextInterval ? nextInterval.min : interval.max;
      }

      const xPos = lowerBound + (upperBound - lowerBound) / 2;
      const area =
        prescriptionState === PrescriptionState.BULK_PRESCRIPTION_OVERVIEW
          ? Number(getBulkPrescriptionIntervalArea(interval, bulkPrescriptionJobs)).toFixed(2)
          : Number(interval.areaFinal).toFixed(2);

      let icon = null;
      if (classificationsEnabled) {
        // Prefer the interval's own classification (persisted/reconstructed
        // on load) over classifying the raw lowerBound value, since for
        // demand jobs lowerBound is a classification bucket index rather
        // than a raw layer value.
        const classification = interval.classification || classifier(lowerBound);
        if (classification) {
          icon = renderStandardDemandLegend(classification, LangFile, layer);
        }
      }

      return (
        <IntervalLabel
          key={idx}
          labelText={area + ' ha'}
          left={isNullInterval(interval) ? (canvasWidth * nullWidth) / 200 : xMap.current(xPos)}
          top={'25px'}
        >
          {Boolean(icon) && (
            <Box
              className={style.DemandIcon}
              sx={{
                mr: 1,
              }}
            >
              {icon}
            </Box>
          )}
        </IntervalLabel>
      );
    });
  }, [
    intervals,
    layer,
    xMap,
    layerConfig,
    classifier,
    LangFile,
    classificationsEnabled,
    isBulkPrescription,
    appliedInitialIntervals,
    bulkPrescriptionJobs,
    effectiveValues,
  ]);

  const redrawCanvas = useCallback(() => {
    const context = ctxRef.current;
    const canvas = canvasRef.current;
    const width = canvas.width;
    const height = canvas.height;

    context.clearRect(0, 0, width, height);
    const nullWidthPixels = (width * nullWidth) / 100;
    if (nullWidthPixels > 0) {
      context.fillStyle = PRESCRIPTION_NULL_INTERVAL_COLOR;
      context.fillRect(0, 0, nullWidthPixels, height);
    }
    context.save();
    context.translate(nullWidthPixels, 0);
    const sourceWidth = width - nullWidthPixels;
    if (isSurveyLayer(layer)) {
      context.fillStyle = generateSurveyGradient(
        context,
        sourceWidth,
        sourceIntervals,
        coloringStrategy,
        layer,
        effectiveValues
      );
    } else if (isYieldLayer(layer)) {
      context.fillStyle = generateSurveyGradient(
        context,
        sourceWidth,
        sourceIntervals,
        coloringStrategy,
        layer
      );
    } else {
      if (layer === SATELLITE_LAYERS.VITALITY_NDVI) {
        const gradient = generateNdviGradient(context, sourceWidth, sourceIntervals);
        if (!gradient) return;
        context.fillStyle = gradient;
      } else {
        console.error('Unexpected source layer selected. Raw MEAN should only be possible.', layer);
      }
    }
    context.fillRect(0, 0, sourceWidth, height);
    context.restore();
  }, [ctxRef, layer, intervals, sourceIntervals, coloringStrategy, nullWidth]);

  const updateDimensions = useCallback(() => {
    if (intervals) {
      const intervalValues = sourceIntervals.map((interval, index) =>
        index === 0 ? interval.min : interval.max
      );

      // Get the current height and width of the svg parent
      const currentWidth = canvasRef.current.offsetWidth;

      // Calculate the new mappings from domain to pixel space
      if (isSoilLayer(layer)) {
        const totalCount = getAllUniqueValues(effectiveValues).length - nullCount;
        xMap.current = d3
          .scaleLinear()
          .domain([0, totalCount])
          .range([(currentWidth * nullWidth) / 100, currentWidth]);
      } else {
        xMap.current = d3
          .scaleLinear()
          .domain(d3.extent(intervalValues, (d) => d))
          .range([(currentWidth * nullWidth) / 100, currentWidth]);
      }

      forceRedraw[1]({});
      redrawCanvas();
    }
  }, [canvasRef, intervals, redrawCanvas]);

  const generateMinMaxLabels = useCallback(() => {
    if (isSoilLayer(layer)) return null;
    return (
      <>
        <div
          className={hasNullValueInterval ? style.Value : style.LowestValue}
          style={hasNullValueInterval ? { left: `${nullWidth}%` } : undefined}
        >
          {minValue < 0.01 ? Number(minValue).toFixed(0) : minValue}
        </div>
        <div className={style.HighestValue}>{maxValue}</div>
      </>
    );
  }, [layer, minValue, maxValue, hasNullValueInterval, nullWidth]);

  useEvent('resize', updateDimensions);

  useEffect(() => {
    if (canvasRef) {
      ctxRef.current = canvasRef.current.getContext('2d');
    }
  }, [canvasRef]);

  useEffect(() => {
    if (ctxRef.current) {
      updateDimensions();
    }
  }, [updateDimensions]);

  return (
    <Paper className={style.Root} elevation={0}>
      <div className={style.Spacer} />
      <div className={style.Title}>
        {isSurveyLayer(layer) &&
          LangFile.PrescriptionScale.distributionOfArea +
            layerConfig.getName(LangFile) +
            LangFile.PrescriptionScale.distributionOfArea2 +
            overriddenAreaString +
            LangFile.PrescriptionScale.distributionOfArea3}
        {isSatelliteLayer(layer) &&
          LangFile.PrescriptionScale.vegetationIndex +
            overriddenAreaString +
            LangFile.PrescriptionScale.vegetationIndex2}
      </div>
      <canvas width={canvasWidth} height={25} ref={canvasRef}></canvas>
      {generateMinMaxLabels()}
      {generateSeparators()}
      {generateIntervalSizeLabels()}
    </Paper>
  );
};

PrescriptionScale.propTypes = {
  layer: PropTypes.string,
  intervals: PropTypes.array,
  overrideAreas: PropTypes.object,
  values: PropTypes.array,
  classificationsEnabled: PropTypes.bool,
  variationsEnabled: PropTypes.bool,
  isBulkPrescription: PropTypes.bool,
  appliedInitialIntervals: PropTypes.array,
  bulkPrescriptionJobs: PropTypes.object,
};

export default memo(PrescriptionScale);
